import { assign, union } from 'lodash-es';
import { Action } from '@ngrx/store';

export interface Reducer<T> {
    reduce(state: T): T;
}

export interface PayloadAction<P> extends Action {
    readonly payload: P;
}

export abstract class FeatureActionReducer<T> implements Reducer<T>, Action {
    readonly type: string;

    protected constructor(feature: string, name: string) {
        this.type = `[${feature}] ${name}`;
    }

    abstract reduce(state: T): T;
}

export interface EntityState<K, T> {
    idMap: {
        [id: string]: T;
    };
    idSort: K[];
}

export interface EntityUI<G, L> {
    global: G;
    idMap: {
        [id: string]: L;
    };
}

export interface EntityVM<T> {
    entity: T;
}

export interface FullEntityVM<E, EUI> {
    entity: E;
    ui: EUI;
}

export function entityDetailReducer<K, T>(
    state: EntityState<K, T>,
    newEntity: T,
    identifier: string = 'id'
): EntityState<K, T> {
    return {
        idMap: {
            ...state.idMap,
            [newEntity[identifier]]: newEntity
        },
        idSort: union(state.idSort, [newEntity[identifier]])
    };
}

export function entityListReducer<K, T>(
    state: EntityState<K, T>,
    newEntities: T[],
    identifier: string,
    replace: boolean = true
): EntityState<K, T> {
    let stateData: EntityState<K, T>;
    if (replace) {
        stateData = {
            idMap: newEntities.reduce<EntityState<K, T>['idMap']>(
                (obj, entity: T) => {
                    obj[entity[identifier]] = entity;
                    return obj;
                },
                {}
            ),
            idSort: newEntities.map<K>((entity: T) => entity[identifier])
        };
    } else {
        stateData = {
            idMap: {
                ...state.idMap
            },
            idSort: state.idSort.slice(0)
        };
        newEntities.forEach(entity => {
            if (!stateData.idMap[entity[identifier]]) {
                // Append new items to array
                stateData.idSort.push(entity[identifier]);
            }
            stateData.idMap[entity[identifier]] = entity;
            return;
        });
    }

    return assign({}, state, stateData);
}

export function createFeatureReducer<T extends FeatureActionReducer<Y>, Y>(
    type: any,
    initialState: Y
) {
    return (state, action: T) => {
        return action instanceof type
            ? action.reduce(state || initialState)
            : state;
    };
}
